import { Component, ViewChild, OnInit, OnDestroy, ViewContainerRef, ComponentRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ChargingComponent } from '../../../shared/components/charging/charging.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { DataService } from '../../../core/services/data.service';
import { DigitalClientService } from '../../../core/services/digital-client.service';
import { Subject, takeUntil, distinctUntilChanged, filter, merge, firstValueFrom, startWith, combineLatest } from 'rxjs';
import { TipoDocumento } from '../../../core/models/digital-client/TipoDocumento';
import { Departamento } from '../../../core/models/digital-client/Departamento';
import { Municipio } from '../../../core/models/digital-client/Municipio';
import { TipoGenero } from '../../../core/models/digital-client/TipoGenero';
import { EstadoCivil } from '../../../core/models/digital-client/EstadoCivil';
import { QuoteContact } from '../../../core/models/leasing/QuoteContacts';
import { TIPOS_EMPRESA } from '../../../core/models/TipoEmpresa';
import { TIPOS_SOCIEDAD } from '../../../core/models/TipoSociedad';
import { ActividadEconomica } from '../../../core/models/digital-client/ActividadEconomica';
import { TipoOcupacion } from '../../../core/models/digital-client/TipoOcupacion';
import { LeasingService } from '../../../core/services/leasing.service';
import { Pais } from '../../../core/models/digital-client/Pais';
import { TipoMoneda } from '../../../core/models/digital-client/TipoMoneda';
import { ClsPersona } from '../../../core/models/digital-client/ClsPersona';
import { AddPropertiesVehiclesByRole } from '../../../core/models/leasing/AddPropertiesVehiclesByRole';
import { AddSpouse } from '../../../core/models/leasing/AddSpouse';
import { LeasingForm } from '../../../core/models/LeasingForm';
import { ConfirmModalComponent } from '../../../shared/components/modalDynamic/confirm-modal.component';
import { ReferenceRequest } from '../../../core/models/leasing/ReferenceRequest';
import { Opportunity } from '../../../core/models/Opportunity';
import { BusinessLogicService } from '../../../core/services/business-logic.service';
import { ValidateForm } from '../../../core/models/leasing/ValidateForm';
import { DatosBasicos } from '../../../core/models/leasing/createPerson/DatosBasicos';
import { Cargo } from '../../../core/models/leasing/Cargo';
import { UppercaseDirective } from '../../../directives/UppercaseDirective';

interface Step {
  id: number;  // ID original del paso
  name: string;
  showFor: {
    roles: number[];
    personTypes: string[];
  };
  visualOrder?: number; // Nuevo campo para el orden visual
}

interface VisibleStep {
  number: number;
  name: string;
  originalId: number;
}
@Component({
  selector: 'app-application-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ChargingComponent, ModalComponent, UppercaseDirective],
  templateUrl: './application-form.component.html',
  styleUrls: ['./application-form.component.scss']
})
export class ApplicationFormComponent implements OnInit, OnDestroy {
  @ViewChild(ChargingComponent) charging!: ChargingComponent;
  @ViewChild(ModalComponent) modal!: ModalComponent;
  @ViewChild(ConfirmModalComponent) confirmModal!: ConfirmModalComponent;
  private modalRef!: ComponentRef<ConfirmModalComponent>;
  private destroy$ = new Subject<void>();

  currentStep: number = 1;
  role: number | null = null;
  personType: string = '';
  currentYear = new Date().getFullYear();

  activityType: number = 0;
  isPensionado: boolean = false;

  showConceptoOtrosIngresos = false;

  tiposEmpresa = TIPOS_EMPRESA;
  tiposSociedad = TIPOS_SOCIEDAD;
  actividadesEconomicas: ActividadEconomica[] = [];
  sucursalMunicipalities: Municipio[] = [];
  tiposOcupacion: TipoOcupacion[] = [];
  paises: Pais[] = [];
  tiposMoneda: TipoMoneda[] = [];

  // Datos dinámicos
  documentTypes: TipoDocumento[] = [];
  departments: Departamento[] = [];
  expeditionMunicipalities: Municipio[] = [];
  residenceMunicipalities: Municipio[] = [];
  genders: TipoGenero[] = [];
  maritalStatuses: EstadoCivil[] = [];
  spouseDocumentTypes: TipoDocumento[] = [];
  listaCargos: Cargo[] = [];
  inmueble1Municipalities: Municipio[] = [];
  inmueble2Municipalities: Municipio[] = [];

  referenceMunicipalities: Municipio[] = [];

  generalForm!: FormGroup;
  laboralForm!: FormGroup;
  financieraForm!: FormGroup;
  inmuebleForm!: FormGroup;
  vehiculosForm!: FormGroup;
  spouseForm!: FormGroup;
  claimsForm!: FormGroup;
  referencesForm!: FormGroup;
  ppeForm!: FormGroup;
  declarationsForm!: FormGroup;
  authorizationsForm!: FormGroup;

  oficinaMunicipalities: Municipio[] = [];

  // Definición completa de steps con sus condiciones
 private allSteps: Step[] = [
  { id: 1, name: 'General', showFor: { roles: [1, 3, 4], personTypes: ['N', 'J'] } },
  { id: 2, name: 'Laboral y Financiera', showFor: { roles: [1, 3, 4], personTypes: ['N', 'J'] } },
  { id: 3, name: 'Bienes Inmuebles y Vehículos', showFor: { roles: [1, 3, 4], personTypes: ['N', 'J'] } },
  { id: 4, name: 'Cónyuge', showFor: { roles: [1], personTypes: ['N'] } },
  { id: 5, name: 'Referencias', showFor: { roles: [1, 3, 4], personTypes: ['N', 'J'] } },
  { id: 6, name: 'PPE', showFor: { roles: [1], personTypes: ['N'] } },
  { id: 7, name: 'Declaraciones', showFor: { roles: [1], personTypes: ['N', 'J'] } },
  { id: 8, name: 'Autorizaciones', showFor: { roles: [1, 3, 4], personTypes: ['N', 'J'] } }
];

  visibleSteps: VisibleStep[] = [];

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private digitalClientService: DigitalClientService,
    private leasingService: LeasingService,
    private viewContainerRef: ViewContainerRef,
    private businessLogicService: BusinessLogicService
  ) {
    this.laboralForm = this.createLaboralForm();
    this.inmuebleForm = this.createInmuebleForm();
    this.vehiculosForm = this.createVehiculosForm();
    this.spouseForm = this.createSpouseForm();
    this.claimsForm = this.createClaimsForm();
    this.referencesForm = this.createReferencesForm();
    this.ppeForm = this.createPpeForm();
    this.declarationsForm = this.createDeclarationsForm();
    this.authorizationsForm = this.createAuthorizationsForm();
  }


ngOnInit() {
  this.role = this.dataService.getCurrentRole();
  const contacts = this.dataService.getQuoteContactData();
  
  if (contacts) {
    const currentContact = contacts.find(contact => contact.inIdRole === this.role);
    if (currentContact) {
      this.personType = currentContact.strPersonType.toUpperCase();
      
      // Detectar si es pensionado y aplicar el mapeo
      this.activityType = currentContact.inIdNaturalPersonType;
      this.isPensionado = (this.role === 1 || this.role! >= 3) && this.activityType === 2;
      
      this.generalForm = this.createGeneralForm();
      this.financieraForm = this.createFinancieraForm();
      this.updateVisibleSteps();  

      this.preloadApplicationFormData(this.role!);      
    }
  }

  this.loadInitialData();
  this.setupFormListeners();
  this.ppeForm = this.createPpeForm();
  this.setupDeclarationsFormListeners(); 
}

  private updateVisibleSteps() {
    const availableSteps = this.allSteps.filter(step =>
      step.showFor.roles.includes(this.role!) &&
      step.showFor.personTypes.includes(this.personType)
    );

    this.visibleSteps = availableSteps.map((step, index) => ({
      number: index + 1,
      name: step.name,
      originalId: step.id
    }));

    if (this.currentStep > this.visibleSteps.length) {
      this.currentStep = 1;
    }
  }

  cargarCargos(): void {
    this.leasingService.getCargos().subscribe({
      next: (res) => {
        if (res.status && res.value) {
          this.listaCargos = res.value;
        }
      },
      error: () => {
        console.error('Error al cargar los cargos');
      }
    });
  }

  async onSubmit() {
    if (this.currentStep < this.visibleSteps.length) {
      // Encontrar el siguiente paso disponible
      const nextStepIndex = this.visibleSteps.findIndex(s => s.number === this.currentStep + 1);
      if (nextStepIndex !== -1) {
        this.currentStep = this.visibleSteps[nextStepIndex].number;
      }
    } else {
      this.charging.showSpinner("Cargando información...");
      if (this.role === 1) {
        await this.arrendatatio();
        await this.createOpportunityTenant();
        await this.createValidationForm(this.role);
      }
      if (this.role === 3 || this.role === 4 || this.role === 5) {
        await this.codeudor();
        await this.createOpportunityTenant();
        await this.createValidationForm(this.role);
      }

      this.charging.hideSpinner();
      this.openConfirmationModal();
    }
  }

  async createValidationForm(idRole: number) {
    const response = this.dataService.getLeasingFormData()
    const quote = this.dataService.getQuoteData()
    const personData = response?.find(response => response.idRol == idRole)
    const validateForm: ValidateForm = {
      id: 0,
      idRole: idRole,
      quotationNumber: quote?.inIdQuoteHeader!,
      idPerson: personData?.persona.datos_Basicos.inIdPersona!,
      dateValidateForms: new Date().toISOString(),
      stateValidateForms: true,
      isEmailSent: false
    };

    this.leasingService.createValidateForm(validateForm).subscribe(result => {
      const response = result;
    })
  }

  async createFormularioPersona() {
    const laboral = this.laboralForm.getRawValue();
    const financiera = this.financieraForm.value;
    const pdp = this.declarationsForm.value;
    const reclamos = this.claimsForm.value;
    const publica = this.ppeForm.value;
    const extranjera = this.financieraForm.value; 
    const general = this.generalForm.value;
    const quote = this.dataService.getQuoteData()
    const declarations = this.declarationsForm.value;

    const persona = this.dataService.getLeasingFormData();
    const personaData = persona?.find(persona => persona.idRol == this.role)
    const generoTexto = general.gender === '0' ? 'M' :general.gender === '1' ? 'F' :'M';
    const datos: DatosBasicos = {
      idCotizacion: quote?.inIdQuoteHeader!,
      idRol: this.role!,
    
      primerNombre: general.firstName?.toUpperCase() || '',
      segundoNombre: general.secondName?.toUpperCase() || '',
      primerApellido: general.firstLastName?.toUpperCase() || '',
      segundoApellido: general.secondLastName?.toUpperCase() || '',
      codigoTipoDocumento: personaData?.persona.datos_Basicos.codigoTipoDocumento || 1,
      numeroDocumento: general.documentNumber || '',
      lugarNacimiento: general.birthplace || '',
      codigoDepartamentoResidencia: general.department || 0,
      codigoMunicipioResidencia: general.city || 0,
      dtFechaExpedicion: general.expeditionDate || 0,
      fechaNacimiento: general.birthDate,
      email: general.email || '',
      telefonoCelular: general.mobilePhone || 0,
      numeroDireccionResidencia: general.address || '',
      strCodigoMunicipioExpDoc: general.expeditionCity || 0,
      strCodigoDepartamentoExpDoc: general.expeditionDepartment || 0,
      inCodigoPais: general.pais || 0,
      telefonoDirecto: general.phone || 0,
      genero: generoTexto || 0, 
      estadoCivil: general.maritalStatus ? String(general.maritalStatus) : '',
      preguntasPDP: {
        blDatosFinanancieros: true,
        blDatosMenores: true,
        blDatosSensibles: true,
        blDatosInternacionales: true,
      },
    
      informacionReclamacionSiniestro: {
        inARecla: reclamos.year || 0,
        strRamoRecla: reclamos.branch || '',
        strCompaniaRecla: reclamos.company || '',
        inValorRecla: parseFloat(String(reclamos.value || '0').replace(/,/g, '')) || 0,
        btReclamacionRecla: reclamos.hasClaim || false,
        btIndemnizacionRecla: reclamos.hasCompensation || false
      },
    
      informacionPersonaPublica: {
        btReconocimientoPublico: publica.publicRecognition || false,
        btRecursosPublicos: publica.holdsPublicOffice || false,
        btFamiAsocPeps: publica.isPpeFamilyMember || false,
        ppE1Documento: publica.ppeDocumentNumber1 || '',
        ppE1Nombre: publica.entityName1 || '',
        ppE1Vinculo: publica.ppeRelation1 || '',
        ppE2Documento: publica.ppeDocumentNumber2 || '',
        ppE2Nombre: publica.entityName2 || '',
        ppE2Vinculo: publica.ppeRelation2 || '',
        ppE3Documento: publica.ppeDocumentNumber3 || '',
        ppE3Nombre: publica.entityName3 || '',
        ppE3Vinculo: publica.ppeRelation3 || '',
      },
 
      informacionFinanciera: {
        dcIngresoMensual: parseFloat(String(financiera.ingresosMensuales || '0').replace(/,/g, '')) || 0,
        dcTotalEgresosMensuales: parseFloat(String(financiera.totalEgresoMensual || '0').replace(/,/g, '')) || 0,
        dcTotalActivos: parseFloat(String(financiera.totalActivos || '0').replace(/,/g, '')) || 0, 
        dcTotalPasivos: parseFloat(String(financiera.totalPasivos || '0').replace(/,/g, '')) || 0,  
        dcOtrosIngresosMensuales: parseFloat(String(financiera.otrosIngresos || '0').replace(/,/g, '')) || 0,   
        conceptoOtrosIngresos: financiera.conceptoOtros || '',
        origenRecursos: financiera.origenRecursos || '',
        btAutorizacionConsultaCentralesRiesgo: financiera.autoriza || false,
        btTransaccionModenaExt: declarations.actividadExtranjera || false,
        btObligacionTributariaUs: declarations.obligacionesEEUU || false
      },
      
      actividadMonedaExtranjera: {
        btExportacionesMonedaExt: declarations.exportaciones || false,
        btImportacionesMonedaExt: declarations.importaciones || false,
        btInversionesExt: declarations.inversiones || false,
        btTransferenciasExt: declarations.transferencias || false,
        btPrestamoExt: declarations.prestamos || false,
        btPagoServiciosExt: declarations.pagoServicios || false,
        btOtrosExt: declarations.otro || false,
        detalleOtroExt: declarations.detalleOtros || '',
        ciudadExt: declarations.ciudad || '',
        paisExt: declarations.paisMoneda || 0,
        monedaExt: declarations.moneda || 0,
        strTipoProductoExt: declarations.tipoProducto || '',
        entidadExt: declarations.entidad || '',
        montoExt: parseFloat(String(declarations.monto || '0').replace(/,/g, '')) || 0,
        strIdentificadorProductoExt: declarations.identificacionProducto || ''
      },

      informacionLaboral: {
        codigoActividadEconomica: laboral.actividadEconomica,
        codigoOcupacion: laboral.profesion?.toString() || '',
        nombreEmpresaTrabajo: laboral.nombreEmpresa || '',
        nombreDepartamentoTrabajo: laboral.areaTrabajo || '',
        nombrePuestoTrabajo: this.listaCargos.find(c => c.codCargo === this.laboralForm.get('cargo')?.value)?.txtDesc || '',
        telefonoOficina: laboral.telefonoOficina || '',
        codigoDepartamentoOficina: laboral.departamentoOficina?.toString() || 0,
        codigoMunicipioOficina: laboral.ciudadOficina?.toString() || 0,
        numeroDireccionOficina: laboral.direccionOficina || ''
      }
    };
    
  
    this.leasingService
      .saveFormulario(datos)
      .subscribe(response => {
        if (response.status) {
        } else {
          console.error('Error en la respuesta del API', response.msn);
        }
      });

      this.leasingService
      .saveFormularioSISE(datos)
      .subscribe(response => {
        if (response.status) {
        } else {
          console.error('Error en la respuesta del API', response.msn);
        }
      });
  }
  
  onlyNumbers(event: Event, controlPath: string): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^0-9]/g, '');
    input.value = cleaned;
    this.vehiculosForm.get(controlPath)?.setValue(cleaned);
  }

  // Metodo para enviar los datos al CRM.
  async createOpportunityTenant() {
    const general = this.generalForm.value;
    const laboral = this.laboralForm.value;
    const financiera = this.financieraForm.value;
    const inmueble = this.inmuebleForm.value;
    const vehiculos = this.vehiculosForm.value;
    const spouse = this.spouseForm.value;
    const claims = this.claimsForm.value;
    const references = this.referencesForm.value;
    const ppe = this.ppeForm.value;
    const declarations = this.declarationsForm.value;
    const quote = this.dataService.getQuoteData();
    const persona = this.dataService.getLeasingFormData();
    const personaData = persona?.find(persona => persona.idRol == this.role)

    const isEmpty = (obj: Record<string, any>) =>
      Object.values(obj).every(val => val === null || val === '' || val === 0);

    const spouseData = isEmpty(spouse) ? null : {
      documentTypeCode: spouse.idDocumentType || 0,
      document: spouse.documentNumber || '',
      expeditionDate: spouse.expeditionDate || '1990-01-01',
      email: spouse.email || '',
      firstName: spouse?.firstName?.toUpperCase() || '',
      middleName: spouse?.secondName?.toUpperCase() || '',
      lastName: spouse?.firstLastName?.toUpperCase() || '',
      secondLastName: spouse?.secondLastName?.toUpperCase() || '',
      companyName: spouse?.companyName?.toUpperCase() || '',
      officeAddress: spouse?.officeAddress?.toUpperCase() || '',
      phone: spouse.officePhone || 0, // Cambiado a officePhone que sí existe
      occupationCode: spouse.occupation || 0,
      salary: spouse?.ingreso ? spouse.ingreso.replace(/,/g, '') : '0', // Cambiado de salary a ingreso
      propertyOwnerName: '' // Campo eliminado, enviar vacío
    };

    const opportunity: Opportunity = {
      opportunityId: quote?.uidOpportunityId.toString() || '',
      actors: [
        {
          roleCode: this.role || 0,
          general: {
            documentTypeCode: personaData?.persona.datos_Basicos.codigoTipoDocumento || 1,
            document: general.documentNumber || '',
            expeditionDeptCode: general.expeditionDepartment || 0,
            expeditionPlaceCode: general.expeditionCity || 0,
            expeditionDate: general.expeditionDate || '1990-01-01',
            firstName: general.firstName?.toUpperCase() || '',
            middleName: general.secondName?.toUpperCase() || '',
            lastName: general.firstLastName?.toUpperCase() || '',
            secondLastName: general.secondLastName?.toUpperCase() || '',
            birthDate: general.birthDate,
            nationalityType: general.nationalityType?.toString().toUpperCase() || "N/A",
            birthPlace: general.birthplace || '',
            address: general.address || '',
            landlinePhone: general.phone || 0,
            cellPhone: general.mobilePhone || 0,
            email: general.email || '',
            deptCode: general.department || 0,
            municipalityCode: general.city || 0,
            genderCode: general.gender || 0,
            maritalStatusCode: general.maritalStatus || 0,
            mailingAddress: general.mailingAddress || '',
            monthlyFamilyExpenses: 0, //parseFloat(String(general.monthlyExpenses || '0').replace(/,/g, '')) || 0,
            numberOfPeople: general.dependents || 0,
            personType: this.personType === "N" ? 2 : 1
          },
          work: {
            economicActivityCode: this.activityType! + 1, // Corresponde al ID de Empleado, Independiente o Pensionado. (enviamos 1+ ya que en crm es diferente)
            ciiuCode: laboral.codigoCiiu?.toString() || '1',
            companyName: laboral.nombreEmpresa || '',
            workArea: laboral.areaTrabajo || '',
            occupation: parseInt(laboral.profesion, 10) || 0,
            position: parseInt(laboral.cargo) || 0,
            entryDate: laboral.fechaIngreso || '1990-01-01',
            officeAddress: laboral.direccionOficina || '',
            officePhone: laboral.telefonoOficina || 0,
            officeDeptCode: laboral.departamentoOficina || 0,
            officeMunicipalityCode: laboral.ciudadOficina || 0
          },
          financial: {
            monthlyIncome: parseFloat(String(financiera.ingresosMensuales || '0').replace(/,/g, '')) || 0,
            otherIncome: parseFloat(String(financiera.otrosIngresos || '0').replace(/,/g, '')) || 0,
            totalMonthlyIncome: parseFloat(String(financiera.totalIngresoMensual || '0').replace(/,/g, '')) || 0,
            totalMonthlyExpenses: parseFloat(String(financiera.totalEgresoMensual || '0').replace(/,/g, '')) || 0,
            totalAssets: parseFloat(String(financiera.totalActivos || '0').replace(/,/g, '')) || 0,
            totalLiabilities: parseFloat(String(financiera.totalPasivos || '0').replace(/,/g, '')) || 0,
            foreignCurrencyAssets: declarations.actividadExtranjera || false,
            taxesInUSA: declarations.obligacionesEEUU || false,
            imports: declarations.importaciones || false,
            exports: declarations.exportaciones || false,
            investments: declarations.inversiones || false,
            transfers: declarations.transferencias || false,
            loans: declarations.prestamos || false,
            servicePayments: declarations.pagoServicios || false,
            other: declarations.otro || false,
            products: declarations.tipoProducto ? [{
              productType: declarations.tipoProducto || '',
              productId: declarations.identificacionProducto || '',
              entity: declarations.entidad || '',
              amount: parseFloat(String(declarations.monto || '0').replace(/,/g, '')) || 0,
              city: declarations.ciudad || '',
              country: Number(declarations.paisMoneda) || 0,
              currency: Number(declarations.moneda) || 0
            }] : []
          },      
          realEstate: [
            {
              propertyRegistryNumber: inmueble.inmueble1.matricula || '',
              propertyAddress: inmueble.inmueble1.direccion || '',
              propertyDeptCode: inmueble.inmueble1.departamento || 0,
              propertyMunicipalityCode: inmueble.inmueble1.ciudad || 0
            },
            {
              propertyRegistryNumber: inmueble.inmueble2.matricula || '',
              propertyAddress: inmueble.inmueble2.direccion || '',
              propertyDeptCode: inmueble.inmueble2.departamento || 0,
              propertyMunicipalityCode: inmueble.inmueble2.ciudad || 0
            }
          ],
          vehicles: [
            {
              brand: vehiculos.vehiculo1.marca || '',
              model: vehiculos.vehiculo1.modelo || 0,
              plate: vehiculos.vehiculo1.placa || ''
            },
            {
              brand: vehiculos.vehiculo2.marca || '',
              model: vehiculos.vehiculo2.modelo || 0,
              plate: vehiculos.vehiculo2.placa || ''
            }
          ],
          spouse: spouseData,
          claims: [
            {
              year: claims.year || 0,
              branch: claims.branch || '',
              company: claims.company || '',
              value: parseFloat((claims.value ? String(claims.value).replace(/,/g, '') : '0')) || 0,
              claim: claims.claim || false,
              compensation: claims.compensation || false
            }
          ],
          references: this.personType === 'J' 
            ? [{ // Persona Jurídica
                type: 3,
                name: references.razonSocial?.toUpperCase() || '',
                phone: references.telefono || '',
                account: 0,
                branch: '',
                state: '',
                city: '',
                relationship: '',
                address: '', 
                currentQuota: 0,
                monthlyQuota: 0
              }]
            : [{ // Persona Natural
                type: references.tipoReferencia || 2,
                name: references.nombreCompleto?.toUpperCase() || '',
                state: references.departamento || '',
                city: references.ciudad || '',
                phone: references.telefono || '',
                relationship: references.parentesco || '',
                account: 0,
                branch: '',
                address: '', 
                currentQuota: 0,
                monthlyQuota: 0
              }],
          ppe:
          {
            publicRecognition: ppe.publicRecognition || false,
            holdsPublicOffice: ppe.holdsPublicOffice || false,
            isPpeFamilyMember: ppe.isPpeFamilyMember || false,
            ppeDocumentNumber: ppe.ppeDocumentNumber || 0,
            entityName: ppe.entityName || '',
            ppeRelation: ppe.ppeRelation || ''
          },

          incomeSource: declarations.incomeSource,
          authorization1: true,
          authorization2: true,
          authorization3: true,
          authorization4: true
        }
      ],
      owner: null,
    };

    this.leasingService.sendOpportunity(opportunity).subscribe(result => {
      const response = result;
    })
  }

  async openConfirmationModal() {
    this.viewContainerRef.clear();
    this.modalRef = this.viewContainerRef.createComponent(ConfirmModalComponent);

    this.modalRef.instance.openModal(
      'Proceso Exitoso',
      'Se han realizado con éxito los pasos del formulario. Se generará el documento para la firma electrónica y recibirá un email donde podrá continuar con el proceso.',
      'Sí, continuar',
      'Cancelar',
      () => this.handleAccept(this.role!),
      () => this.handleCancel()
    );
  }

  async handleAccept(role: number) {
    // Suscribirse al evento close antes de cualquier error
    const subscription = this.modal.close.subscribe(() => {
      this.dataService.changeView('home');
      subscription.unsubscribe();
    });
  
    try {
      this.charging.showSpinner("Generando documento para firma electrónica...");
      await this.businessLogicService.sendSign(role);
  
      this.modal.openModal(
        'El documento fue generado exitosamente.',
        'Recibirá un email donde podrá continuar con el proceso de firma electrónica.',
        true
      );
    } catch (error) {
      this.modal.openModal(
        'Ha ocurrido un error',
        'Ha ocurrido un error en el proceso de la generación del documento para la firma electrónica',
        false
      );
    } finally {
      this.charging.hideSpinner();
    }
  }

  async handleCancel() {
    await this.dataService.changeView('home');
  }


  convertToISODate(dateStr: string): string {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  }


  // Metodo para precargar la informacion de todo el formulario de 11 pasos.
  private async preloadApplicationFormData(idRole: number): Promise<void> {

    // Trata de recuperar TODA la informacion de los 11 pasos del FindPersonFuccById con el id del FindUser
    const response = this.dataService.getLeasingFormData()
    const personData = response?.find(response => response.idRol == idRole)

    // Trae la data de contacto (solo la de general)
    const info = this.dataService.getQuoteContactData();
    const infoData = info?.find(info => info.inIdRole == idRole)

    const basicInfo = personData?.persona.datos_Basicos;
    const financialInfo = personData?.persona.informacion_Financiera;
    const laboralInfo = personData?.persona.informacion_Laboral;
    const publicInfo = personData?.persona.informacion_Persona_Publica;
    const claimInfo = personData?.persona.informacion_Reclamaciones_En_Siniestros;
    const pdpInfo = personData?.persona.preguntas_PDP;

    if (infoData?.inCodDepartment) {
      // Cargar municipios y esperar la respuesta
      await this.loadMunicipalitiesAndSetValue(infoData.inCodDepartment.toString(), 'residence', infoData.inCodCity);
    }
    
    if (!personData) {
      this.generalForm.reset();
      this.generalForm.patchValue({
        idDocumentType: infoData?.inCodIdentificationType ?? null,
        documentNumber: infoData?.inContactIdentification ?? '',
        firstLastName: infoData?.strContactFirstLastName?.toUpperCase() ?? '',
        secondLastName: infoData?.strContactSecondLastName?.toUpperCase() ?? '',
        firstName: infoData?.strContactFirstName?.toUpperCase() ?? '',
        secondName: infoData?.strContactSecondName?.toUpperCase() ?? '',
        expeditionDate: null, // Pendiente almacenar
        expeditionDepartment: null, // Pendiente almacenar
        expeditionCity: null, // Pendiente almacenar
        birthplace: '', // Pendiente almacenar
        birthDate: this.convertToISODate(infoData?.dT_Birthday!), // !!!!!!!!!!!
        nationalityType: null, // Pendiente almacenar
        gender: 0, // Pendiente almacenar
        maritalStatus: null, // Pendiente almacenar
        address: infoData?.strContactAddress?.toUpperCase() ?? '',
        department: infoData?.inCodDepartment ?? null,
        city: infoData?.inCodCity ?? null,
        phone: infoData?.strContactTelephoneNumber ?? '',
        mobilePhone: infoData?.strContactMobileTelephoneNumber ?? '',
        email: infoData?.strContactEmail ?? '',
        mailingAddress: infoData?.strContactAddress?.toUpperCase() ?? ''
      });
      return;
    }
    else{
      this.generalForm.get('idDocumentType')?.disable();
      this.generalForm.patchValue({
        idDocumentType: basicInfo?.codigoTipoDocumento ?? infoData?.inCodIdentificationType,
        documentNumber: basicInfo?.numeroDocumento,
        firstLastName: basicInfo?.primerApellido?.toUpperCase() || '',
        secondLastName: basicInfo?.segundoApellido?.toUpperCase() || '',
        firstName: basicInfo?.primerNombre?.toUpperCase() || '',
        secondName: basicInfo?.segundoNombre?.toUpperCase() || '',
        expeditionDate: null,
        expeditionDepartment: null,
        expeditionCity: null,
        birthplace: basicInfo?.lugarNacimiento,
        birthDate: this.convertToISODate(basicInfo?.fechaNacimiento ?? "01/01/1900") ?? "01/01/1900",
        nationalityType: '',//basicInfo?.inCodigoPais,
        gender: basicInfo?.genero === "M" ? "0" : "1",
        maritalStatus: basicInfo?.estadoCivil == 0 ? 1 : basicInfo?.estadoCivil,
        address: infoData?.strContactAddress?.toUpperCase() || basicInfo?.numeroDireccionResidencia || '',
        department: basicInfo?.codigoDepartamentoResidencia,
        city: basicInfo?.codigoMunicipioResidencia,
        phone: basicInfo?.telefonoDirecto,
        mobilePhone: basicInfo?.telefonoCelular,
        email: basicInfo?.email,
        mailingAddress: infoData?.strContactAddress?.toUpperCase() || basicInfo?.numeroDireccionResidencia || '',
      });

      if(!this.isPensionado)
      {
        this.laboralForm.patchValue({
          actividadEconomica: laboralInfo?.codigoActividadEconomica,
          codigoCiiu: '',
          nombreEmpresa: laboralInfo?.nombreEmpresaTrabajo,
          areaTrabajo: laboralInfo?.nombreDepartamentoTrabajo,
          profesion: laboralInfo?.codigoOcupacion === 0 ? null : laboralInfo?.codigoOcupacion,
          cargo: null,
          direccionOficina: laboralInfo?.numeroDireccionOficina,
          telefonoOficina: laboralInfo?.telefonoOficina,
          departamentoOficina: null,
          ciudadOficina: null
        });
      }
      else{
         // Si es pensionado, mapear datos del step 1 al laboral y formatear labels
        this.mapGeneralToLaboralForPensionado(infoData);
        this.setupPensionadoLaboralForm();
      }
      
      this.financieraForm.patchValue({
        ingresosMensuales: financialInfo?.dcIngresoMensual || '0',
        otrosIngresos: financialInfo?.dcOtrosIngresosMensuales || '0',
        conceptoOtrosIngresos: financialInfo?.conceptoOtrosIngresos,
        totalIngresoMensual: financialInfo?.dcTotalIngresosMensuales,
        totalEgresoMensual: financialInfo?.dcTotalEgresosMensuales,
        totalActivos: financialInfo?.dcTotalActivos,
        totalPasivos: financialInfo?.dcTotalPasivos
      });

      this.ppeForm.patchValue({
        publicRecognition: publicInfo?.btReconocimientoPublico,
        holdsPublicOffice: publicInfo?.btRecursosPublicos,
        isPpeFamilyMember: publicInfo?.btFamiAsocPeps,
        ppeDocumentNumber1: publicInfo?.ppE1Documento,
        entityName1: publicInfo?.ppE1Nombre,
        ppeRelation1: publicInfo?.ppE1Vinculo,
        ppeDocumentNumber2: publicInfo?.ppE2Documento,
        entityName2: publicInfo?.ppE2Nombre,
        ppeRelation2: publicInfo?.ppE2Vinculo,
        ppeDocumentNumber3: publicInfo?.ppE3Documento,
        entityName3: publicInfo?.ppE3Nombre,
        ppeRelation3: publicInfo?.ppE3Vinculo
      });


      this.claimsForm.patchValue({
        year: claimInfo?.inARecla,
        branch: claimInfo?.strRamoRecla,
        company: claimInfo?.strCompaniaRecla,
        value: claimInfo?.inValorRecla,
        hasClaim: claimInfo?.btReclamacionRecla,
        hasCompensation: claimInfo?.btIndemnizacionRecla
      });

      this.declarationsForm.patchValue({
        financialData: pdpInfo?.blDatosFinanancieros,
        minorData: pdpInfo?.blDatosMenores,
        sensitiveData: pdpInfo?.blDatosSensibles,
        internationalData: pdpInfo?.blDatosInternacionales,
        incomeSource: financialInfo?.origenRecursos,
        actividadExtranjera: financialInfo?.btTransaccionModenaExt,
        obligacionesEEUU: financialInfo?.btObligacionTributariaUs
      });

      this.authorizationsForm.patchValue({
        autorizacionCentrales: true,
        authA: pdpInfo?.blDatosFinanancieros == -1 ? "si" : "no",
        authB: pdpInfo?.blDatosMenores == -1 ? "si" : "no",
        authC: pdpInfo?.blDatosSensibles == -1 ? "si" : "no",
        authD: pdpInfo?.blDatosInternacionales == -1 ? "si" : "no"
      })
    }
  }

  async arrendatatio() {
    try {
      await this.createFormularioPersona()
      await this.sendPropertiesVehiclesData();
      await this.sendSpouseData();
      await this.sendReferences();
      await this.enviarDatosAdiccionales();
      await this.createPerson();
    } catch (error) {
      console.error('Error enviando los datos', error);
    }
  }

  async createPerson() {
    try {
      const value = this.createPersonPayload()
      const response = await this.digitalClientService.CreatePerson(value).toPromise();
      return response;
    } catch (error) {
      console.error('Error en createPerson:', error);
      throw error;
    }
  }

  async sendPropertiesVehiclesData() {
    try {
      const payload = this.createPropertiesVehiclesPayload();
      const response = await this.leasingService.AddOrUpdatePropertiesVehiclesByRol(payload).toPromise();
    } catch (error) {
      console.error('Error enviando propiedades y vehículos:', error);
    }
  }

  async sendSpouseData() {
    try {
      const payload = this.createSpousePayload();
      const response = await this.leasingService.AddSpouseByQuoteHeader(payload).toPromise();
    } catch (error) {
      console.error('Error enviando datos del cónyuge:', error);
    }
  }


  async sendReferences() {
    if (!this.referencesForm.valid) {
      console.warn("Formulario de referencias no es válido.");
      return;
    }
    const contacts = this.dataService.getQuoteContactData();
    const quote = contacts?.find(contact => contact.inIdRole === this.role);
    const formValue = this.referencesForm.value;

    let referencesData: ReferenceRequest[];

    if (this.personType === 'J') {
      // Persona Jurídica: Referencia Comercial (tipo 3)
      referencesData = [{
        inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
        inIdRole: this.role || 0,
        inIdReferenceType: 3,
        strAdress: '',
        strReferenceName: formValue.razonSocial || '',
        branch: '',
        entity: '',
        inCodDeparment: 0,
        inCodCity: 0,
        strTelephoneNumber: formValue.telefono || '',
        strRelationship: '',
        strAccountNumber: '',
        InIdReference: 0,
      }];
    } else {
      // Persona Natural: Referencia Familiar/Personal (tipo 2)
      referencesData = [{
        inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
        inIdRole: this.role || 0,
        inIdReferenceType: formValue.tipoReferencia || 3,
        strAdress: '',
        strReferenceName: formValue.nombreCompleto || '',
        branch: '',
        entity: '',
        inCodDeparment: formValue.departamento || 0,
        inCodCity: formValue.ciudad || 0,
        strTelephoneNumber: formValue.telefono || '',
        strRelationship: formValue.parentesco || '',
        strAccountNumber: '',
        InIdReference: 0,
      }];
    }

    try {
      const response = await this.leasingService.addReferences(referencesData).toPromise();
      if (response?.status) {
      } else {
        console.error("Error en la respuesta del servicio:", response!.msn);
      }
    } catch (error) {
      console.error("Error al enviar referencias:", error);
    }
  }



  createSpousePayload(): AddSpouse {
    const contacts = this.dataService.getQuoteContactData();
    const quote = contacts?.find(contact => contact.inIdRole === this.role)
    const generoTexto = this.spouseForm.get('gender')?.value === '0' ? 'M' : 'F'

    return {
      inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
      dtDocumentExpeditionDate: this.getFormattedDate(this.spouseForm.get('expeditionDate')?.value, '1900-01-01'),
      strEmail: this.spouseForm.get('email')?.value || '',
      strFirstName: this.spouseForm.get('firstName')?.value || '',
      strSecondName: this.spouseForm.get('secondName')?.value || '',
      strFirstLastName: this.spouseForm.get('firstLastName')?.value || '',
      strSecondLastName: this.spouseForm.get('secondLastName')?.value || '',
      strWorkplaceName: this.spouseForm.get('companyName')?.value || '',
      strWorkplaceTelephone: this.spouseForm.get('officePhone')?.value || '',
      strWorkplaceAdress: this.spouseForm.get('officeAddress')?.value || '',
      inCodEconomicActivity: this.spouseForm.get('occupation')?.value || 0,
      dcSalary: Number((this.spouseForm.get('ingreso')?.value || '0').replace(/\,/g, '')) || 0,
      strOwnerNameSpouseResides: '',
      strOwnerTelephoneSpouseResides: '',
      inContactIdentification: this.spouseForm.get('documentNumber')?.value || '',
      inCodIdentificationType: this.spouseForm.get('idDocumentType')?.value || 0,
      strExpeditionCity: this.spouseForm.get('expeditionCity')?.value || '',
      strBirthplace: this.spouseForm.get('birthplace')?.value || '',
      dtBirthDate: this.getFormattedDate(this.spouseForm.get('birthDate')?.value, '1900-01-01'),
      strNationality: this.spouseForm.get('nationalityType')?.value || '',
      strGender: generoTexto
    };
  }

  getFormattedDate(date: any, defaultDate: string = '1900-01-01'): string {
    if (!date) return defaultDate;
    return new Date(date).toISOString().split('T')[0];
  }



  createPropertiesVehiclesPayload(): AddPropertiesVehiclesByRole {
    const contacts = this.dataService.getQuoteContactData();
    const quote = contacts?.find(contact => contact.inIdRole === this.role);
    const vehiculos = this.vehiculosForm.value;
    const inmuebles = this.inmuebleForm.value;

    const propiedades = [
      {
        inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
        inIdRole: this.role || 0,
        strAdress: inmuebles.inmueble1?.direccion || '',
        inDeparmentCod: inmuebles.inmueble1?.departamento ? Number(inmuebles.inmueble1.departamento) : 0,
        inCityCod: inmuebles.inmueble1?.ciudad ? Number(inmuebles.inmueble1.ciudad) : 0,
        strRegistrationNumber: inmuebles.inmueble1?.matricula || '',
        dcComercialValue:  0,
        dcMortgageValue: 0
      },
      {
        inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
        inIdRole: this.role || 0,
        strAdress: inmuebles.inmueble2?.direccion || '',
        inDeparmentCod: inmuebles.inmueble2?.departamento ? Number(inmuebles.inmueble2.departamento) : 0,
        inCityCod: inmuebles.inmueble2?.ciudad ? Number(inmuebles.inmueble2.ciudad) : 0,
        strRegistrationNumber: inmuebles.inmueble2?.matricula || '',
        dcComercialValue: 0,
        dcMortgageValue: 0
      }
    ];

    const propiedadesValidas = propiedades.filter(p => {
      const filledFields = [
        p.strAdress, p.inDeparmentCod, p.inCityCod,
        p.strRegistrationNumber, p.dcComercialValue, p.dcMortgageValue
      ].filter(value => value && value !== 0 && value !== '');
      
      return filledFields.length >= 3;
    });
  
    return {
      listProperties: propiedadesValidas, 
      listVehicle: [
        {
          inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
          inIdRole: this.role || 0,
          strBrand: vehiculos.vehiculo1?.marca || '',
          strModel: vehiculos.vehiculo1?.modelo || '',
          strLicensePlateNumber: vehiculos.vehiculo1?.placa || '',
          blPawnVehicle: false // Campo eliminado del formulario
        },
        {
          inIdQuoteHeader: quote?.inIdQuoteHeader || 0,
          inIdRole: this.role || 0,
          strBrand: vehiculos.vehiculo2?.marca || '',
          strModel: vehiculos.vehiculo2?.modelo || '',
          strLicensePlateNumber: vehiculos.vehiculo2?.placa || '',
          blPawnVehicle: false // Campo eliminado del formulario
        }
      ]
    };
  }
  


  createPersonPayload(): ClsPersona {
    const financiera = this.financieraForm.value;
    return {
      preguntas_PDP: {
        blDatosFinanancieros: this.generalForm.get('financialData')?.value || 0,
        blDatosMenores: this.generalForm.get('minorData')?.value || 0,
        blDatosSensibles: this.generalForm.get('sensitiveData')?.value || 0,
        blDatosInternacionales: this.generalForm.get('internationalData')?.value || 0,
      },
      informacion_Reclamaciones_En_Siniestros: {
        inARecla: 0,
        strRamoRecla: '',
        strCompaniaRecla: '',
        inValorRecla: 0,
        btReclamacionRecla: false,
        btIndemnizacionRecla: false,
      },
      informacion_Persona_Publica: {
        btReconocimientoPublico: this.ppeForm.get('publicRecognition')?.value || false,
        btRecursosPublicos: this.ppeForm.get('holdsPublicOffice')?.value || false,
        btFamiAsocPeps: this.ppeForm.get('isPpeFamilyMember')?.value || false,
        ppE1Documento: this.ppeForm.get('ppeDocumentNumber1')?.value || '',
        ppE1Nombre: this.ppeForm.get('entityName1')?.value || '',
        ppE1Vinculo: this.ppeForm.get('ppeRelation1')?.value || '',
        ppE2Documento: this.ppeForm.get('ppeDocumentNumber2')?.value || '',
        ppE2Nombre: this.ppeForm.get('entityName2')?.value || '',
        ppE2Vinculo: this.ppeForm.get('ppeRelation2')?.value || '',
        ppE3Documento: this.ppeForm.get('ppeDocumentNumber3')?.value || '',
        ppE3Nombre: this.ppeForm.get('entityName3')?.value || '',
        ppE3Vinculo: this.ppeForm.get('ppeRelation3')?.value || '',
      },
      informacion_Financiera: {
        dcIngresoMensual: parseFloat(String(financiera.ingresosMensuales || '0').replace(/,/g, '')) || 0,
        dcTotalIngresosMensuales: parseFloat(String(financiera.totalIngresoMensual || '0').replace(/,/g, '')) || 0,
        dcTotalEgresosMensuales: parseFloat(String(financiera.totalEgresoMensual || '0').replace(/,/g, '')) || 0,
        dcTotalActivos: parseFloat(String(financiera.totalActivos || '0').replace(/,/g, '')) || 0,
        dcTotalPasivos: parseFloat(String(financiera.totalPasivos || '0').replace(/,/g, '')) || 0,
        dcOtrosIngresosMensuales: parseFloat(String(financiera.otrosIngresos || '0').replace(/,/g, '')) || 0,
        conceptoOtrosIngresos: this.financieraForm.get('conceptoOtrosIngresos')?.value || '',
        origenRecursos: this.financieraForm.get('incomeSource')?.value || '',
        btAutorizacionConsultaCentralesRiesgo: true,
        btTransaccionModenaExt: this.declarationsForm.get('actividadExtranjera')?.value || false,
        btObligacionTributariaUs: this.declarationsForm.get('obligacionesEEUU')?.value || false,
        informacion_Actividad_Moneda_Extrangera: {
          btImportacionesMonedaExt: this.declarationsForm.get('importaciones')?.value || false,
          btExportacionesMonedaExt: this.declarationsForm.get('exportaciones')?.value || false,
          btInversionesExt: this.declarationsForm.get('inversiones')?.value || false,
          btTransferenciasExt: this.declarationsForm.get('transferencias')?.value || false,
          btPrestamoExt: this.declarationsForm.get('prestamos')?.value || false,
          btPagoServiciosExt: this.declarationsForm.get('pagoServicios')?.value || false,
          btOtrosExt: this.declarationsForm.get('otro')?.value || false,
          detalleOtroExt: '',
          ciudadExt: this.declarationsForm.get('ciudad')?.value || '',
          paisExt: this.declarationsForm.get('paisMoneda')?.value || 0,
          monedaExt: this.declarationsForm.get('moneda')?.value || 0,
          strTipoProductoExt: this.declarationsForm.get('tipoProducto')?.value || '',
          entidadExt: this.declarationsForm.get('entidad')?.value || '',
          montoExt: parseFloat(String(this.declarationsForm.get('monto')?.value || '0').replace(/,/g, '')) || 0,
          strIdentificadorProductoExt: this.declarationsForm.get('identificacionProducto')?.value || '',
        }
      },
      informacion_Laboral: {
        codigoActividadEconomica: this.laboralForm.get('actividadEconomica')?.value || 0,
        codigoOcupacion: this.laboralForm.get('profesion')?.value || 0,
        nombreEmpresaTrabajo: this.laboralForm.get('nombreEmpresa')?.value || '',
        nombreDepartamentoTrabajo: this.laboralForm.get('areaTrabajo')?.value || '',
        nombrePuestoTrabajo: this.listaCargos.find(c => c.codCargo === this.laboralForm.get('cargo')?.value)?.txtDesc || '',
        telefonoOficina: this.laboralForm.get('telefonoOficina')?.value || '',
        codigoDepartamentoOficina: this.laboralForm.get('departamentoOficina')?.value || 0,
        codigoMunicipioOficina: this.laboralForm.get('ciudadOficina')?.value || 0,
        numeroDireccionOficina: this.laboralForm.get('direccionOficina')?.value || '',
      },
      datos_Basicos: {
        inIdPersona: this.generalForm.get('idPersona')?.value || 0,
        inIdUsuario: 0,
        inCodigoPais: 0,
        codigoDepartamentoResidencia: this.generalForm.get('department')?.value || 0,
        codigoMunicipioResidencia: this.generalForm.get('city')?.value || 0,
        codigoDepartamentoDil: 0,
        codigoMunicipioDil: 0,
        estadoCivil: this.generalForm.get('maritalStatus')?.value || 0,
        codigoTipoDocumento: this.generalForm.get('idDocumentType')?.value || 0,
        strCodigoDepartamentoExpDoc: this.generalForm.get('expeditionDepartment')?.value || 0,
        strCodigoMunicipioExpDoc: this.generalForm.get('expeditionCity')?.value || 0,
        numeroDireccionResidencia: this.generalForm.get('address')?.value || '',
        dtFechaExpedicion: this.generalForm.get('expeditionDate')?.value || '',
        genero: 'M',
        lugarNacimiento: this.generalForm.get('birthplace')?.value || '',
        fechaNacimiento: this.generalForm.get('birthDate')?.value || '',
        numeroDocumento: this.generalForm.get('documentNumber')?.value || '',
        primerApellido: this.generalForm.get('firstLastName')?.value || '',
        primerNombre: this.generalForm.get('firstName')?.value || '',
        segundoApellido: this.generalForm.get('secondLastName')?.value || '',
        segundoNombre: this.generalForm.get('secondName')?.value || '',
        email: this.generalForm.get('email')?.value || '',
        telefonoDirecto: this.generalForm.get('phone')?.value || '',
        telefonoCelular: this.generalForm.get('mobilePhone')?.value || '',
        telefonoAdicional: this.generalForm.get('additionalPhone')?.value || '',
      },
      vinculos_Poliza: null,
      gobernabilidad: [],
    };
  }

  async codeudor(){
    try {
      await this.createFormularioPersona();
      await this.createPerson();
      await this.sendPropertiesVehiclesData();
      await this.sendReferences();
      await this.enviarDatosAdiccionales();
    } catch (error) {
      console.error('Error enviando los datos', error);
    }
  }

async enviarDatosAdiccionales(){
  const laboral = this.laboralForm.value;
  const quotationNumber = this.dataService.getQuotationNumber();
  
  // Obtener el JSON existente
  const data = await firstValueFrom(
    this.leasingService.FindLinkQuotePerson(quotationNumber!, this.role!)
  );

  let linkJson: any = { f0: {}, f1: {} };

  // Si existe información previa, preservar f0
  if (data?.value?.strJson) {
    try {
      const existingJson = JSON.parse(data.value.strJson);
      // Preservar completamente f0 existente
      linkJson.f0 = existingJson.f0 || {};
    } catch (error) {
      console.error('Error parsing existing JSON:', error);
    }
  }

  // Agregar/actualizar solo f1 con los datos adicionales
  linkJson.f1 = {
    DdlTipoNacionalidad: this.generalForm.get('nationalityType')?.value || '',
    TxtDirEnvioCorrespondencia: this.generalForm.get('mailingAddress')?.value || '', 
    intGastoFamiliar: 0,//parseFloat((this.generalForm.get('monthlyExpenses')?.value || '0').toString().replace(/,/g, '')) || 0,
    intNroPersonas: Number(this.generalForm.get('dependents')?.value) || 0,
    intCodCiiu: laboral.codigoCiiu || 0,
    TxtFechaIngreso: laboral.fechaIngreso || '01/01/1900',
    infGeneral: true
  };

  const blUpdateLinkPerson = !!(data?.value && (
    Array.isArray(data.value) ? data.value.length > 0 : true
  ));

  const linkRequest = {
    inIdRole: this.role!,
    inIdQuoteHeader: quotationNumber!,
    strJson: JSON.stringify(linkJson),
    blUpdateLinkPerson: blUpdateLinkPerson
  };

  await firstValueFrom(
    this.leasingService.AddOrUpdateLinkQuotePerson(linkRequest)
  );
}

  async goBack(): Promise<void> {
    await this.dataService.changeView('home', null);
  }

  private createGeneralForm(): FormGroup {
    if (this.personType === 'N') {
      return this.fb.group({
            idDocumentType: [null, [Validators.required]],
            documentNumber: ['', [Validators.required]],
            firstLastName: ['', [Validators.required]],
            secondLastName: [''],
            firstName: ['', [Validators.required]],
            secondName: [''],
            expeditionDate: [null, [Validators.required]],
            expeditionDepartment: [null, [Validators.required]],
            expeditionCity: [{ value: null, disabled: true }, [Validators.required]],
            birthplace: ['', [Validators.required]],
            birthDate: [null, [Validators.required]],
            nationalityType: new FormControl(null, [Validators.required, Validators.pattern(/^[A-Za-z\s]+$/)]),
            gender: [null, [Validators.required]],
            maritalStatus: [null, [Validators.required]],
            address: ['', [Validators.required]],
            department: [null, [Validators.required]],
            city: [{ value: null, disabled: true }, [Validators.required]],
            phone: ['',  [Validators.required]],
            mobilePhone: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
            mailingAddress: ['', [Validators.required]],
            monthlyExpenses: [0],
            dependents: [0, [Validators.required]]
          });  
    }
    else {
        return this.fb.group({
          idDocumentType: [null, [Validators.required]],
          documentNumber: ['', [Validators.required]],
          firstLastName: ['', [Validators.required]],
          email: ['', [Validators.required, Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
          tipoEmpresa: [null, [Validators.required]],
          tipoSociedad: [null, [Validators.required]],
          actividadEconomica: [null, [Validators.required]],
          codigoCiiu: [''],
          address: ['', [Validators.required]],
          phone: ['', [Validators.required]],
          department: [null, [Validators.required]],
          city: [{ value: null, disabled: true }, [Validators.required]],
          direccionSucursal: [null],
          telefonoSucursal: [null],
          departamentoSucursal: [null, [Validators.required]],
          ciudadSucursal: [{ value: null, disabled: true }, [Validators.required]],
        });
    } 
  }

  private createLaboralForm(): FormGroup {
    return this.fb.group({
      actividadEconomica: [null, Validators.required],
      codigoCiiu: [''],
      nombreEmpresa: ['', Validators.required],
      areaTrabajo: [''],
      profesion: [null, Validators.required],
      cargo: [null, [Validators.required]],
      fechaIngreso: [null, Validators.required],
      direccionOficina: ['', Validators.required],
      telefonoOficina: ['', Validators.required],
      departamentoOficina: [null, Validators.required],
      ciudadOficina: [{ value: null, disabled: true }, Validators.required],
      actividadExtranjera: [false],
      importaciones: [false],
      exportaciones: [false],
      inversiones: [false],
      transferencias: [false],
      prestamos: [false],
      pagoServicios: [false],
      otro: [false],
      tipoProducto: [''],
      identificacionProducto: [''],
      entidad: [''],
      monto: [''],
      paisMoneda: [null],
      ciudad: [''],
      moneda: [null]
    });
  }

private createFinancieraForm(): FormGroup {
  if (this.personType === 'J') {
    // Persona Jurídica
    return this.fb.group({
      ingresosMensuales: [0, [Validators.required, Validators.min(1)]],
      egresosMensuales: [0, [Validators.required, Validators.min(1)]],
      activos: [0, [Validators.required, Validators.min(1)]],
      pasivos: [0, [Validators.required, Validators.min(1)]],
      patrimonio: [0, [Validators.required, Validators.min(1)]]
    });
  }

  // Persona Natural
  const form = this.fb.group({
    ingresosMensuales: [0, [Validators.required, Validators.min(1)]],
    otrosIngresos: [0],
    conceptoOtrosIngresos: [''],
    totalIngresoMensual: [0, Validators.required],
    totalEgresoMensual: [0, [Validators.required, Validators.min(1)]],
    totalActivos: [0],
    totalPasivos: [0, [Validators.required, Validators.min(1)]]
  });

  // Calcular total de ingresos
  combineLatest([
    form.get('ingresosMensuales')!.valueChanges.pipe(startWith(form.get('ingresosMensuales')!.value)),
    form.get('otrosIngresos')!.valueChanges.pipe(startWith(form.get('otrosIngresos')!.value))
  ]).pipe(
    takeUntil(this.destroy$)
  ).subscribe(() => {
    this.calcularTotalIngresos();
  });

  // Validación condicional de conceptoOtrosIngresos
  form.get('otrosIngresos')!.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      const numValue = Number((value || '').toString().replace(/,/g, '')) || 0;
      const conceptoControl = form.get('conceptoOtrosIngresos');
      
      if (numValue > 0) {
        conceptoControl?.setValidators([Validators.required]);
        this.showConceptoOtrosIngresos = true;
      } else {
        conceptoControl?.clearValidators();
        conceptoControl?.setValue('');
        this.showConceptoOtrosIngresos = false;
      }
      conceptoControl?.updateValueAndValidity();
    });

  return form;
}

  private calcularTotalIngresos(): void {
    // Verificar que el formulario existe antes de usarlo
    if (!this.financieraForm) {
      return;
    }

    // Obtener valores de forma segura
    const ingresosValue = this.financieraForm.get('ingresosMensuales')?.value;
    const otrosValue = this.financieraForm.get('otrosIngresos')?.value;
    
    // Verificar que los valores existen y son strings antes de usar replace
    const ingresosMensuales = Number(
      (typeof ingresosValue === 'string' ? ingresosValue : String(ingresosValue || 0)).replace(/,/g, '')
    ) || 0;
    
    const otrosIngresos = Number(
      (typeof otrosValue === 'string' ? otrosValue : String(otrosValue || 0)).replace(/,/g, '')
    ) || 0;

    const total = ingresosMensuales + otrosIngresos;

    // Verificar que el formulario sigue existiendo antes de actualizar
    if (this.financieraForm) {
      this.financieraForm.patchValue({
        totalIngresoMensual: this.formatNumberValue(total)
      }, { emitEvent: false });
    }
  }

  private formatNumberValue(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private createInmuebleForm(): FormGroup {
    return this.fb.group({
      inmueble1: this.fb.group({
        matricula: [''],
        direccion: [''],
        departamento: [null],
        ciudad: [{ value: null, disabled: true }]
      }),
      inmueble2: this.fb.group({
        matricula: [''],
        direccion: [''],
        departamento: [null],
        ciudad: [{ value: null, disabled: true }]
      })
    });
  }

  private createVehiculosForm(): FormGroup {
    return this.fb.group({
      vehiculo1: this.fb.group({
        marca: [''],
        modelo: [''],
        placa: ['']
      }),
      vehiculo2: this.fb.group({
        marca: [''],
        modelo: [''],
        placa: ['']
      })
    });
  }

  private createSpouseForm(): FormGroup {
    return this.fb.group({
      idDocumentType: [null],
      documentNumber: [''],
      expeditionDate: [null],
      expeditionCity: [''],
      birthplace: [''],
      birthDate: [null],
      nationalityType: [''],
      gender: [null],
      email: ['', [Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
      firstLastName: [''],
      secondLastName: [''],
      firstName: [''],
      secondName: [''],
      companyName: [''],
      officeAddress: [''],
      officePhone: [''],
      occupation: [null],
      ingreso: ['']
    });
  }

  private createClaimsForm(): FormGroup {
    return this.fb.group({
      year: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
      branch: [''],
      company: [''],
      value: [''],
      hasClaim: [false],
      hasCompensation: [false]
    });
  }

  private createReferencesForm(): FormGroup {
    if (this.personType === 'J') {
      // Persona Jurídica: Solo Referencia Comercial
      return this.fb.group({
        razonSocial: ['', Validators.required],
        telefono: ['', Validators.required]
      });
    } else {
      // Persona Natural: Solo Referencia Familiar/Personal
      return this.fb.group({
        tipoReferencia: [null, Validators.required],
        nombreCompleto: ['', Validators.required],
        departamento: [null, Validators.required],
        ciudad: [{ value: null, disabled: true }, Validators.required],
        parentesco: ['', Validators.required],
        telefono: ['', Validators.required]
      });
    }
  }

  private createPpeForm(): FormGroup {
    return this.fb.group({
      publicRecognition: [false],
      holdsPublicOffice: [false],
      isPpeFamilyMember: [false],
      // PPE 1
      ppeDocumentNumber1: [''],
      entityName1: [''],
      ppeRelation1: [''],
      // PPE 2
      ppeDocumentNumber2: [''],
      entityName2: [''],
      ppeRelation2: [''],
      // PPE 3
      ppeDocumentNumber3: [''],
      entityName3: [''],
      ppeRelation3: ['']
    });
  }


private createDeclarationsForm(): FormGroup {
  return this.fb.group({
    incomeSource: ['', Validators.required],
    financialData: [false],
    minorData: [false],
    sensitiveData: [false],
    internationalData: [false],
    actividadExtranjera: [false],
    obligacionesEEUU: [false],
    importaciones: [{ value: false, disabled: true }],
    exportaciones: [{ value: false, disabled: true }],
    inversiones: [{ value: false, disabled: true }],
    transferencias: [{ value: false, disabled: true }],
    prestamos: [{ value: false, disabled: true }],
    pagoServicios: [{ value: false, disabled: true }],
    otro: [{ value: false, disabled: true }],
    tipoProducto: [{ value: '', disabled: true }],
    identificacionProducto: [{ value: '', disabled: true }],
    entidad: [{ value: '', disabled: true }],
    monto: [{ value: '', disabled: true }],
    paisMoneda: [{ value: null, disabled: true }],
    ciudad: [{ value: '', disabled: true }],
    moneda: [{ value: null, disabled: true }]
  });
}

  private createAuthorizationsForm(): FormGroup {
    return this.fb.group({
      autorizacionCentrales: [false, Validators.requiredTrue],
      authA: ['si'],
      authB: ['si'],
      authC: ['si'],
      authD: ['si']
    });
  }

  private loadInitialData() {
    this.loadDocumentTypes();
    this.loadDepartments();
    this.loadGenders();
    this.loadMaritalStatuses();
    this.loadOcupaciones();
    this.cargarCargos();
    this.loadActividadesEconomicas();
    this.loadPaises();
    this.loadTiposMoneda();
  }
  private setupFormListeners() {
    // Listener para departamento de expedición
    this.generalForm.get('expeditionDepartment')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.generalForm.get('expeditionCity');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value, 'expedition');
        }
      });

    // Listener para departamento de residencia
    this.generalForm.get('department')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.generalForm.get('city');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value.toString(), 'residence');
        }
      });

    this.generalForm.get('departamentoSucursal')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.generalForm.get('ciudadSucursal');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value, 'sucursal');
        }
      });

    // En setupFormListeners(), agregar:
    this.laboralForm.get('departamentoOficina')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.laboralForm.get('ciudadOficina');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value, 'oficina');
        }
      });

    this.inmuebleForm.get('inmueble1.departamento')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.inmuebleForm.get('inmueble1.ciudad');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value, 'inmueble1');
        }
      });
    this.inmuebleForm.get('inmueble2.departamento')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value !== null)
      )
      .subscribe(value => {
        const municipalityControl = this.inmuebleForm.get('inmueble2.ciudad');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          this.loadMunicipalities(value, 'inmueble2');
        }
      });

    this.setupPpeFormListeners();
  }

  private loadDocumentTypes() {
    const personTypeNumber = this.personType === 'N' ? 1 : 2;
    this.digitalClientService.GetTiposDocumentoConParametro(personTypeNumber)
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.documentTypes = response.value;
          }
        },
        error: (error) => console.error('Error loading document types:', error)
      });

    this.digitalClientService.GetTiposDocumento()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.spouseDocumentTypes = response.value;
          }
        },
        error: (error) => console.error('Error loading spouse document types:', error)
      });
  }

  loadDepartments() {
    this.digitalClientService.GetDepartamentosByPais()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.departments = response.value;
          }
        },
        error: (error) => console.error('Error loading departments:', error)
      });
  }

  private loadMunicipalities(departmentId: string, type: 'expedition' | 'residence' | 'sucursal' | 'oficina' | 'inmueble1' | 'inmueble2') {
    this.digitalClientService.GetMunicipiosPorDepartamento(departmentId)
      .subscribe({
        next: (response) => {
          if (response.value) {
            switch (type) {
              case 'expedition':
                this.expeditionMunicipalities = response.value;
                break;
              case 'oficina':
                this.oficinaMunicipalities = response.value;
                break;
              case 'sucursal':
                this.sucursalMunicipalities = response.value;
                break;
              case 'inmueble1':
                this.inmueble1Municipalities = response.value;
                break;
              case 'inmueble2':
                this.inmueble2Municipalities = response.value;
                break;
              default:
                this.residenceMunicipalities = response.value;
            }
          }
        },
        error: (error) => console.error('Error loading municipalities:', error)
      });
  }

  loadGenders() {
    this.digitalClientService.GetTiposGeneros()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.genders = response.value;
          }
        },
        error: (error) => console.error('Error loading genders:', error)
      });
  }

  loadMaritalStatuses() {
    this.digitalClientService.GetEstadosCivil()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.maritalStatuses = response.value;
          }
        },
        error: (error) => console.error('Error loading marital statuses:', error)
      });
  }

  private loadOcupaciones() {
    this.digitalClientService.GetTiposOcupacion()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.tiposOcupacion = response.value;
          }
        },
        error: (error) => console.error('Error loading ocupaciones:', error)
      });
  }

formatNumber(event: any) {
  // quita todo lo que no sea dígito
  let value = event.target.value.replace(/\D/g, '');
  // elimina ceros a la izquierda, pero deja al menos un 0 si el número es "0"
  value = value.replace(/^0+(?!$)/, '');
  value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  event.target.value = value;
  const controlName = event.target.getAttribute('formControlName');
  this.financieraForm.get(controlName)?.setValue(value);
}

  onActividadEconomicaChange(): void {
    const selectedCodigoActividad = this.laboralForm.get('actividadEconomica')?.value;
    if (selectedCodigoActividad) {
      const actividad = this.actividadesEconomicas.find(
        a => a.codigoActividadEconomica == selectedCodigoActividad
      );
  
      if (actividad) {
        this.laboralForm.patchValue({
          codigoCiiu: actividad.codigoActividadEconomica || ''
        });
      } else {
        this.laboralForm.patchValue({
          codigoCiiu: ''
        });
      }
    } else {
      this.laboralForm.patchValue({
        codigoCiiu: ''
      });
    }
  }

    onActividadEconomicaChangeJuridica() {
    const selected = this.actividadesEconomicas.find(a => 
      a.codigoActividadEconomica === this.generalForm.get('actividadEconomica')?.value
    );
    if (selected) {
      this.generalForm.patchValue({ codigoCiiu: selected.codigoActividadEconomica });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  private loadActividadesEconomicas() {
    this.digitalClientService.GetActivadesEconomicas()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.actividadesEconomicas = response.value;
            this.onActividadEconomicaChange();
          }
        },
        error: (error) => console.error('Error loading actividades economicas:', error)
      });
  }

  private setupPpeFormListeners(): void {
    this.ppeForm.get('isPpeFamilyMember')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((checked) => {
        const controls = ['ppeDocumentNumber', 'entityName', 'ppeRelation'];
        controls.forEach(control => {
          const formControl = this.ppeForm.get(control);
          if (checked) {
            formControl?.enable();
          } else {
            formControl?.disable();
            formControl?.setValue('');
          }
        });
      });
  }

  private handleActividadExtranjera(checked: boolean | null) {
    if (checked === null) return;

    const controls = [
      'importaciones', 'exportaciones', 'inversiones', 'transferencias',
      'prestamos', 'pagoServicios', 'otro', 'tipoProducto',
      'identificacionProducto', 'entidad', 'monto', 'paisMoneda',
      'ciudad', 'moneda'
    ];

    controls.forEach(control => {
      const formControl = this.financieraForm.get(control);
      if (checked) {
        formControl?.enable();
      } else {
        formControl?.disable();
        formControl?.setValue(control.includes('Moneda') ? null : '');
      }
    });
  }

  private loadPaises() {
    this.digitalClientService.GetPaises().subscribe({
      next: (response) => {
        if (response?.value) {
          this.paises = response.value;
        }
      }
    });
  }

  private loadTiposMoneda() {
    this.digitalClientService.GetTiposMonedas().subscribe({
      next: (response) => {
        if (response?.value) {
          this.tiposMoneda = response.value;
        }
      }
    });
  }

  getAllFormData(): any {
    const requestData = {
      generalInfo: this.generalForm.value,
      laboralInfo: this.laboralForm.value,
      financialInfo: this.financieraForm.value,
      inmuebleInfo: this.inmuebleForm.value,
      vehiculosInfo: this.vehiculosForm.value,
      spouseInfo: this.spouseForm.value,
      claimsInfo: this.claimsForm.value,
      referencesInfo: this.referencesForm.value,
      ppeInfo: this.ppeForm.value,
      declarationsInfo: this.declarationsForm.value,
      //authorizationsInfo: this.authorizationsForm.value
    };
    return requestData;
  }

  allowOnlyLetters(event: KeyboardEvent) {
  const char = event.key;
  const regex = /^[A-Za-z\s]*$/;
  if (!regex.test(char)) {
    event.preventDefault();
  }
}

// Nuevo método para mapear datos de General a Laboral para pensionados
private async mapGeneralToLaboralForPensionado(currentContact: any) {
  // Mapear campos del step 1 (General) al laboralForm
  this.laboralForm.patchValue({
    direccionOficina: currentContact.strContactAddress || '',
    telefonoOficina: currentContact.strContactTelephoneNumber || '',
    departamentoOficina: currentContact.inCodDepartment || null,
    ciudadOficina: currentContact.inCodCity || null
  });
  
  // Cargar municipios para el departamento mapeado si existe
  if (currentContact.inCodDepartment) {
    try {
      const response = await firstValueFrom(
        this.digitalClientService.GetMunicipiosPorDepartamento(currentContact.inCodDepartment.toString())
      );
      
      if (response?.value) {
        this.oficinaMunicipalities = response.value;
        // Re-establecer el valor de la ciudad después de cargar los municipios
        if (currentContact.inCodCity) {
          this.laboralForm.patchValue({
            ciudadOficina: currentContact.inCodCity
          });
        }
      }
    } catch (error) {
      console.error('Error loading office municipalities for pensionado:', error);
    }
  }
}

// Nuevo método para configurar el formulario laboral para pensionados
private setupPensionadoLaboralForm() {
  // Setear actividad económica como Pensionado o Jubilado (id 91)
  this.laboralForm.patchValue({
    actividadEconomica: 91,
    codigoCiiu: '91',
    areaTrabajo: 'NO APLICA',
    cargo: 99 // Otros
  });
  
  // Deshabilitar el campo área de trabajo para que no se pueda modificar
  this.laboralForm.get('areaTrabajo')?.disable();
  this.laboralForm.get('actividadEconomica')?.disable();
}

// Método para actualizar labels dinámicamente
getPensionadoLabels() {
  if (this.isPensionado) {
    return {
      nombreEmpresa: 'Entidad que paga la pensión',
      fechaIngreso: 'Fecha de Resolución de Pensión',
      direccionOficina: 'Dirección',
      telefonoOficina: 'Teléfono', 
      departamentoOficina: 'Departamento',
      ciudadOficina: 'Ciudad/Municipio'
    };
  }
  return {
    nombreEmpresa: 'Nombre Empresa',
    fechaIngreso: 'Fecha de Ingreso',
    direccionOficina: 'Dirección Oficina',
    telefonoOficina: 'Teléfono Oficina',
    departamentoOficina: 'Departamento Oficina', 
    ciudadOficina: 'Ciudad/Municipio Oficina'
  };
}

// Método para manejar cambio de departamento de oficina
onDepartamentoOficinaChange(): void {
  const selectedDepartment = this.laboralForm.get('departamentoOficina')?.value;
  if (selectedDepartment) {
    // Limpiar ciudad seleccionada
    this.laboralForm.get('ciudadOficina')?.setValue(null);
    this.laboralForm.get('ciudadOficina')?.enable();
    
    // Cargar municipios del departamento seleccionado
    this.digitalClientService.GetMunicipiosPorDepartamento(selectedDepartment.toString())
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.oficinaMunicipalities = response.value;
          }
        },
        error: (error) => console.error('Error loading office municipalities:', error)
      });
  } else {
    // Si no hay departamento, deshabilitar ciudad
    this.laboralForm.get('ciudadOficina')?.disable();
    this.laboralForm.get('ciudadOficina')?.setValue(null);
    this.oficinaMunicipalities = [];
  }
}

// Método para manejar cambio de departamento en referencias
onDepartamentoReferenciaChange(): void {
  const selectedDepartment = this.referencesForm.get('departamento')?.value;
  if (selectedDepartment) {
    // Limpiar ciudad seleccionada
    this.referencesForm.get('ciudad')?.setValue(null);
    this.referencesForm.get('ciudad')?.enable();
    
    // Cargar municipios del departamento seleccionado
    this.digitalClientService.GetMunicipiosPorDepartamento(selectedDepartment.toString())
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.referenceMunicipalities = response.value;
          }
        },
        error: (error) => console.error('Error loading reference municipalities:', error)
      });
  } else {
    // Si no hay departamento, deshabilitar ciudad
    this.referencesForm.get('ciudad')?.disable();
    this.referencesForm.get('ciudad')?.setValue(null);
    this.referenceMunicipalities = [];
  }
}

  private async loadMunicipalitiesAndSetValue(departmentId: string, type: string, cityValue: any): Promise<void> 
  {
    try {
      const response = await firstValueFrom(
        this.digitalClientService.GetMunicipiosPorDepartamento(departmentId)
      );
      
      if (response?.value) {
        this.residenceMunicipalities = response.value;
        
        // Ahora sí establecer el valor de la ciudad
        const municipalityControl = this.generalForm.get('city');
        if (municipalityControl && cityValue) {
          municipalityControl.enable();
          municipalityControl.setValue(cityValue);
        }
      }
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  }

private setupDeclarationsFormListeners(): void {
  this.declarationsForm.get('actividadExtranjera')?.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe((checked) => {
      const controls = [
        'importaciones', 'exportaciones', 'inversiones', 'transferencias',
        'prestamos', 'pagoServicios', 'otro', 'tipoProducto',
        'identificacionProducto', 'entidad', 'monto', 'paisMoneda',
        'ciudad', 'moneda'
      ];

      controls.forEach(control => {
        const formControl = this.declarationsForm.get(control);
        if (checked) {
          formControl?.enable();
        } else {
          formControl?.disable();
          formControl?.setValue(control.includes('Moneda') ? null : '');
        }
      });
    });
}
}